import type { ApplicantCreation } from 'cpool-schemas';
import type { Path } from 'react-hook-form';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { LuTrash2 } from 'react-icons/lu';

import { AbloesenSection } from './shared/AbloesenSection';
import { TextInput } from 'cpool-components/input/TextInput';

import { FormDateInput } from '~/features/leadDetails/components/Overview/CustomerData/shared/inputs/FormDateInput';
import { FormCurrencyInput } from '~/features/leadDetails/components/Overview/CustomerData/shared/inputs/FormCurrencyInput';
import { FormNumberInput } from '~/features/leadDetails/components/Overview/CustomerData/shared/inputs/FormNumberInput';
import { FormPercentInput } from '~/features/leadDetails/components/Overview/CustomerData/shared/inputs/FormPercentInput';
import { SONSTIGE_VERBINDLICHKEITEN } from '../../../util/types';

type Props = {
    index: number;
    onRemove: () => void;
};

export const SonstigeVerbindlichkeit: React.FC<Props> = ({ index, onRemove }) => {
    const { t } = useTranslation();
    const { control, register } = useFormContext<ApplicantCreation>();

    return (
        <div className="rounded bg-white p-2.5 space-y-3">
            <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold text-midnight">
                    {`${t(`EXISTING_LOANS.TYPES.${SONSTIGE_VERBINDLICHKEITEN}`)} ${index + 1}`}
                </h4>
                <button
                    type="button"
                    onClick={onRemove}
                    className="p-2 text-alert-error hover:bg-alert-error/10 rounded"
                >
                    <LuTrash2 />
                </button>
            </div>

            <div className="grid grid-cols-2 gap-3">
                <FormCurrencyInput
                    id={`${SONSTIGE_VERBINDLICHKEITEN}.${index}.urspruenglicherKreditbetrag` as Path<ApplicantCreation>}
                    label={t('EXISTING_LOANS.LOAN_ORIGINAL_AMOUNT')}
                    control={control}
                    min={0}
                />

                <FormCurrencyInput
                    id={`${SONSTIGE_VERBINDLICHKEITEN}.${index}.rateMonatlich` as Path<ApplicantCreation>}
                    label={t('EXISTING_LOANS.LOAN_MONTHLY_RATE')}
                    control={control}
                    min={0}
                />

                <FormCurrencyInput
                    id={`${SONSTIGE_VERBINDLICHKEITEN}.${index}.restschuld` as Path<ApplicantCreation>}
                    label={t('EXISTING_LOANS.LOAN_RESIDUAL_DEBT')}
                    control={control}
                    min={0}
                />

                <FormCurrencyInput
                    id={`${SONSTIGE_VERBINDLICHKEITEN}.${index}.schlussrate` as Path<ApplicantCreation>}
                    label={t('EXISTING_LOANS.LOAN_FINAL_RATE')}
                    control={control}
                    min={0}
                />

                <FormDateInput
                    id={`${SONSTIGE_VERBINDLICHKEITEN}.${index}.datumErsteZahlung` as Path<ApplicantCreation>}
                    label={t('EXISTING_LOANS.LOAN_FIRST_PAYMENT_DATE')}
                    control={control}
                />

                <FormDateInput
                    id={`${SONSTIGE_VERBINDLICHKEITEN}.${index}.datumLetzteRate` as Path<ApplicantCreation>}
                    label={t('EXISTING_LOANS.LOAN_LAST_RATE_DATE')}
                    control={control}
                />

                <TextInput
                    id={`${SONSTIGE_VERBINDLICHKEITEN}.${index}.glaeubiger`}
                    label={t('EXISTING_LOANS.LOAN_CREDITOR')}
                    labelClassName="w-[175px] justify-start"
                    {...register(`${SONSTIGE_VERBINDLICHKEITEN}.${index}.glaeubiger` as Path<ApplicantCreation>)}
                />

                <FormNumberInput
                    id={`${SONSTIGE_VERBINDLICHKEITEN}.${index}.kreditLaufzeit` as Path<ApplicantCreation>}
                    label={t('EXISTING_LOANS.LOAN_RUNTIME')}
                    control={control}
                    min={0}
                />

                <FormPercentInput
                    id={`${SONSTIGE_VERBINDLICHKEITEN}.${index}.zinssatz` as Path<ApplicantCreation>}
                    label={t('EXISTING_LOANS.LOAN_INTEREST')}
                    control={control}
                    min={0}
                />

                <AbloesenSection basePath={`${SONSTIGE_VERBINDLICHKEITEN}.${index}`} />
            </div>
        </div>
    );
};
